import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import { brandsApi, categoriesApi, productsApi, taxRulesApi, unitsApi } from '@/api';
import type { TaxRuleResponse } from '@/types/taxRule';
import { ProductCatalogNav } from '@/components/org/ProductCatalogNav';
import { PageHeader } from '@/components/layout/PageHeader';
import { useModuleScopedBreadcrumbs, useModuleSharedPath } from '@/hooks/useModuleScopedBreadcrumbs';
import { Button } from '@/components/ui/Button';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { useDebounce } from '@/hooks/useDebounce';
import { usePagination } from '@/hooks/usePagination';
import type { BrandResponse, CategoryResponse, ProductRequest, ProductResponse, UnitRequest, UnitResponse } from '@/types/product';
import { formatCurrency } from '@/utils/format';
import { usePlanLimits } from '@/hooks/usePlanLimits';
import { PlanLimitNotice } from '@/components/ui/PlanLimitNotice';
import { showApiError } from '@/utils/apiError';
import { notify } from '@/core/toast/notify';

const empty: ProductRequest = { sku: '', name: '', sellingPrice: 0 };

export function ProductsPage() {
  const breadcrumbs = useModuleScopedBreadcrumbs('Products');
  const inventoryPath = useModuleSharedPath('inventory');
  const { atProductLimit, productLimitMessage, reload: reloadLimits } = usePlanLimits();
  const pagination = usePagination();
  const debouncedSearch = useDebounce(pagination.search);
  const [data, setData] = useState<ProductResponse[]>([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<ProductResponse | null>(null);
  const [form, setForm] = useState<ProductRequest>(empty);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState<CategoryResponse[]>([]);
  const [brands, setBrands] = useState<BrandResponse[]>([]);
  const [units, setUnits] = useState<UnitResponse[]>([]);
  const [taxRules, setTaxRules] = useState<TaxRuleResponse[]>([]);
  const [selectedTaxRuleId, setSelectedTaxRuleId] = useState<number | ''>('');
  const [metaLoading, setMetaLoading] = useState(false);
  const [quickBrandOpen, setQuickBrandOpen] = useState(false);
  const [quickUnitOpen, setQuickUnitOpen] = useState(false);
  const [quickBrandName, setQuickBrandName] = useState('');
  const [quickUnit, setQuickUnit] = useState<UnitRequest>({ name: '', symbol: '' });
  const [quickSaving, setQuickSaving] = useState(false);

  const loadMetadata = useCallback(async () => {
    setMetaLoading(true);
    try {
      const [c, b, u, t] = await Promise.all([
        categoriesApi.list({ size: 500, activeOnly: true }),
        brandsApi.list({ size: 500, activeOnly: true }),
        unitsApi.list({ size: 500, activeOnly: true }),
        taxRulesApi.list({ size: 200 }),
      ]);
      setCategories(c.content);
      setBrands(b.content);
      setUnits(u.content);
      setTaxRules(t.content);
    } catch (err) {
      showApiError(err, 'Failed to load categories, brands, or units');
    } finally {
      setMetaLoading(false);
    }
  }, []);

  useEffect(() => {
    loadMetadata();
  }, [loadMetadata]);

  useEffect(() => {
    if (modalOpen) loadMetadata();
  }, [modalOpen, loadMetadata]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await productsApi.list({
        page: pagination.page,
        size: pagination.size,
        search: debouncedSearch || undefined,
      });
      setData(res.content);
      pagination.applyPageResponse(res);
    } catch (err) {
      showApiError(err, 'Failed to load products');
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.size, debouncedSearch, pagination]);

  useEffect(() => {
    load();
  }, [load]);

  const categoryName = (id?: number) => categories.find((c) => c.id === id)?.name ?? '—';
  const brandName = (id?: number) => brands.find((b) => b.id === id)?.name ?? '—';
  const unitLabel = (id?: number) => {
    const u = units.find((x) => x.id === id);
    if (!u) return '—';
    return u.symbol ? `${u.name} (${u.symbol})` : u.name;
  };

  const saveQuickBrand = async () => {
    if (!quickBrandName.trim()) {
      notify.error('Brand name is required');
      return;
    }
    setQuickSaving(true);
    try {
      const created = await brandsApi.create({ name: quickBrandName.trim() });
      await loadMetadata();
      setForm((f) => ({ ...f, brandId: created.id }));
      setQuickBrandOpen(false);
      setQuickBrandName('');
      notify.success('Brand added');
    } catch (err) {
      showApiError(err, 'Could not add brand');
    } finally {
      setQuickSaving(false);
    }
  };

  const saveQuickUnit = async () => {
    if (!quickUnit.name.trim()) {
      notify.error('Unit name is required');
      return;
    }
    setQuickSaving(true);
    try {
      const created = await unitsApi.create({
        name: quickUnit.name.trim(),
        symbol: quickUnit.symbol?.trim() || undefined,
      });
      await loadMetadata();
      setForm((f) => ({ ...f, unitId: created.id }));
      setQuickUnitOpen(false);
      setQuickUnit({ name: '', symbol: '' });
      notify.success('Unit added');
    } catch (err) {
      showApiError(err, 'Could not add unit');
    } finally {
      setQuickSaving(false);
    }
  };

  const columns: Column<ProductResponse>[] = [
    { key: 'sku', header: 'SKU' },
    { key: 'barcode', header: 'Barcode' },
    { key: 'name', header: 'Name' },
    { key: 'categoryId', header: 'Category', render: (r) => categoryName(r.categoryId) },
    { key: 'brandId', header: 'Brand', render: (r) => brandName(r.brandId) },
    { key: 'unitId', header: 'Unit', render: (r) => unitLabel(r.unitId) },
    { key: 'sellingPrice', header: 'Price', render: (r) => formatCurrency(r.sellingPrice) },
    { key: 'taxPercentage', header: 'Tax %' },
    { key: 'status', header: 'Status', render: (r) => (r.status ? 'Active' : 'Inactive') },
    {
      key: 'edit',
      header: '',
      render: (r) => (
        <button
          type="button"
          className="text-primary-600"
          onClick={() => {
            setEditing(r);
            setForm({
              sku: r.sku,
              barcode: r.barcode,
              name: r.name,
              description: r.description,
              categoryId: r.categoryId,
              brandId: r.brandId,
              unitId: r.unitId,
              sellingPrice: r.sellingPrice,
              mrp: r.mrp,
              discountPercentage: r.discountPercentage,
            });
            setModalOpen(true);
          }}
        >
          Edit
        </button>
      ),
    },
  ];

  const save = async () => {
    if (!form.sku.trim() || !form.name.trim()) {
      notify.error('SKU and name are required');
      return;
    }
    setSaving(true);
    try {
      const saved = editing
        ? await productsApi.update(editing.id, form)
        : await productsApi.create(form);
      if (selectedTaxRuleId) {
        await taxRulesApi.assignProductTax({
          productId: saved.id,
          taxRuleId: Number(selectedTaxRuleId),
        });
      }
      notify.success('Saved');
      setModalOpen(false);
      setSelectedTaxRuleId('');
      load();
      reloadLimits();
    } catch (err) {
      showApiError(err, 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <PageHeader
        title="Products"
        breadcrumbs={breadcrumbs}
        actions={
          <Button
            disabled={atProductLimit}
            title={atProductLimit ? productLimitMessage ?? '' : undefined}
            onClick={() => {
              setEditing(null);
              setForm(empty);
              setSelectedTaxRuleId('');
              setModalOpen(true);
            }}
          >
            <AddIcon fontSize="small" /> Add product
          </Button>
        }
      />
      {atProductLimit && productLimitMessage && <PlanLimitNotice message={productLimitMessage} />}
      <ProductCatalogNav />
      <DataTable columns={columns}
        data={data}
        keyField="id"
        loading={loading}
        search={pagination.search}
        onSearchChange={(v) => {
          pagination.setSearch(v);
          pagination.resetPage();
        }}
        page={pagination.page}
        totalPages={pagination.totalPages}
        totalElements={pagination.totalElements}
        onPageChange={pagination.setPage}
        emptyTitle="No products yet"
        emptyDescription="Add products to your catalog for POS and inventory."
        emptyActionLabel="Add product"
        onEmptyAction={() => {
          if (atProductLimit) return;
          setEditing(null);
          setForm(empty);
          setModalOpen(true);
        }}
      />
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Edit product' : 'New product'}
        size="lg"
        footer={
          <>
            <Button variant="secondary" onClick={() => setModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={save} loading={saving}>
              Save
            </Button>
          </>
        }
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Input label="SKU *" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} />
          <Input
            label="Barcode"
            value={form.barcode ?? ''}
            onChange={(e) => setForm({ ...form, barcode: e.target.value })}
          />
          <Input
            label="Name *"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="sm:col-span-2"
          />
          <div>
            <Select
              label="Category"
              placeholder={metaLoading ? 'Loading…' : '—'}
              disabled={metaLoading}
              options={categories.map((c) => ({ value: String(c.id), label: c.name }))}
              value={form.categoryId != null ? String(form.categoryId) : ''}
              onChange={(v) => setForm({ ...form, categoryId: v ? Number(v) : undefined })}
            />
            {categories.length === 0 && !metaLoading && (
              <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">
                No categories yet.{' '}
                <Link to="/pos/categories" className="underline">
                  Add categories
                </Link>
              </p>
            )}
          </div>
          <div>
            <div className="mb-1 flex items-end justify-between gap-2">
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Brand</span>
              <button
                type="button"
                className="text-xs text-primary-600 hover:underline"
                onClick={() => setQuickBrandOpen(true)}
              >
                + New brand
              </button>
            </div>
            <Select
              placeholder={metaLoading ? 'Loading…' : '—'}
              disabled={metaLoading}
              options={brands.map((b) => ({ value: String(b.id), label: b.name }))}
              value={form.brandId != null ? String(form.brandId) : ''}
              onChange={(v) => setForm({ ...form, brandId: v ? Number(v) : undefined })}
            />
            {brands.length === 0 && !metaLoading && (
              <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">
                No brands yet. Use &quot;+ New brand&quot; or{' '}
                <Link to="/pos/brands" className="underline">
                  manage brands
                </Link>
                .
              </p>
            )}
          </div>
          <div>
            <div className="mb-1 flex items-end justify-between gap-2">
              <span className="text-sm font-medium text-slate-700 dark:text-slate-300">Unit</span>
              <button
                type="button"
                className="text-xs text-primary-600 hover:underline"
                onClick={() => setQuickUnitOpen(true)}
              >
                + New unit
              </button>
            </div>
            <Select
              placeholder={metaLoading ? 'Loading…' : '—'}
              disabled={metaLoading}
              options={units.map((u) => ({
                value: String(u.id),
                label: u.symbol ? `${u.name} (${u.symbol})` : u.name,
              }))}
              value={form.unitId != null ? String(form.unitId) : ''}
              onChange={(v) => setForm({ ...form, unitId: v ? Number(v) : undefined })}
            />
            {units.length === 0 && !metaLoading && (
              <p className="mt-1 text-xs text-amber-700 dark:text-amber-400">
                No units yet. Use &quot;+ New unit&quot; or{' '}
                <Link to="/pos/units" className="underline">
                  manage units
                </Link>
                .
              </p>
            )}
          </div>
          <p className="col-span-2 text-xs text-slate-500 dark:text-slate-400">
            Cost is updated from{' '}
            <Link to={inventoryPath} className="font-medium text-primary-600 underline">
              Inventory → Purchases
            </Link>
            . Low-stock thresholds are set per store under{' '}
            <Link to="/pos/product-alerts" className="font-medium text-primary-600 underline">
              Product alerts
            </Link>
            .
          </p>
          <Input
            label="Selling price *"
            type="number"
            value={form.sellingPrice}
            onChange={(e) => setForm({ ...form, sellingPrice: Number(e.target.value) })}
          />
          <Input
            label="MRP"
            type="number"
            value={form.mrp ?? ''}
            onChange={(e) => setForm({ ...form, mrp: Number(e.target.value) })}
          />
          <div className="sm:col-span-2">
            <Select
              label="Tax rule (store checkout)"
              placeholder="Default from store country"
              options={taxRules.map((tr) => ({
                value: String(tr.id),
                label: `${tr.taxName} (${tr.taxPercentage}% · ${tr.countryCode})`,
              }))}
              value={selectedTaxRuleId === '' ? '' : String(selectedTaxRuleId)}
              onChange={(v) => setSelectedTaxRuleId(v ? Number(v) : '')}
            />
            <p className="mt-1 text-xs text-slate-500">
              Per-store selling price on{' '}
              <Link to="/pos/store-products" className="underline">Store Products</Link>.
            </p>
          </div>
          <Input
            label="Discount %"
            type="number"
            value={form.discountPercentage ?? ''}
            onChange={(e) => setForm({ ...form, discountPercentage: Number(e.target.value) })}
          />
        </div>
      </Modal>

      <Modal
        open={quickBrandOpen}
        onClose={() => setQuickBrandOpen(false)}
        title="Add brand"
        footer={
          <>
            <Button variant="secondary" onClick={() => setQuickBrandOpen(false)}>
              Cancel
            </Button>
            <Button onClick={saveQuickBrand} loading={quickSaving}>
              Add
            </Button>
          </>
        }
      >
        <Input
          label="Brand name *"
          value={quickBrandName}
          onChange={(e) => setQuickBrandName(e.target.value)}
          autoFocus
        />
      </Modal>

      <Modal
        open={quickUnitOpen}
        onClose={() => setQuickUnitOpen(false)}
        title="Add unit"
        footer={
          <>
            <Button variant="secondary" onClick={() => setQuickUnitOpen(false)}>
              Cancel
            </Button>
            <Button onClick={saveQuickUnit} loading={quickSaving}>
              Add
            </Button>
          </>
        }
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Unit name *"
            value={quickUnit.name}
            onChange={(e) => setQuickUnit({ ...quickUnit, name: e.target.value })}
            autoFocus
          />
          <Input
            label="Symbol"
            value={quickUnit.symbol ?? ''}
            onChange={(e) => setQuickUnit({ ...quickUnit, symbol: e.target.value })}
          />
        </div>
      </Modal>
    </div>
  );
}
